/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { trackButtonClick } from "@/utils/analytics";
import { homeCmsData } from "@/redux/cms/cmsSelectors";
import { buildEventLabel, sanitizeBioText } from "./utils";
import { getProfilePhotoUrl } from "@/api/myProfile";
import BrokerBioModal from "./BrokerBioModal";
import "./broker-spotlight.css";

const DEFAULT_PHOTO = "/assets/images/default_profile.png";
const PHONE_ICON = "/assets/images/pdp/phone_broker.png";
const EMAIL_ICON = "/assets/images/pdp/mail_broker.png";
const WEBSITE_ICON = "/assets/images/pdp/website_broker.png";
const BIO_ICON = "/assets/images/pdp/bio_broker.png";
const LINKEDIN_ICON = "/assets/images/pdp/linkedIn.png";

/**
 * BrokerCard — Internal sub-component displaying a single broker's
 * avatar, identity, role label, and contact row.
 */
const BrokerCard = ({ firstName, lastName, title, company, photoUrl, phone, email, website, linkedInUrl, role, bio, onViewBio, onContact }) => {
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const initial = firstName?.charAt(0)?.toUpperCase() || "?";
  const [showContactPopup, setShowContactPopup] = useState(false);

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = DEFAULT_PHOTO;
  };

  return (
    <div className="broker-spotlight__card">
      <div className="broker-spotlight__card-left">
        {/* Avatar */}
        {photoUrl ? (
          <div className="broker-spotlight__avatar">
            <img
              src={photoUrl}
              alt={`${fullName} profile photo`}
              onError={handleImageError}
            />
          </div>
        ) : (
          <div className="broker-spotlight__avatar-initial" aria-hidden="true">
            {initial}
          </div>
        )}
      </div>

      <div className="broker-spotlight__card-center">
        <h3 className="broker-spotlight__name">{fullName}</h3>
        {title && <p className="broker-spotlight__broker-title">{title}</p>}
        {company && <p className="broker-spotlight__company">{company}</p>}

        {/* Contact details as text rows */}
        <div className="broker-spotlight__contact-list">
          {phone && (
            <a href={`tel:${phone}`} className="broker-spotlight__contact-item" aria-label={`Call broker at ${phone}`}>
              <img src={PHONE_ICON} alt="" className="broker-spotlight__contact-icon-img" />
              <span>{phone}</span>
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} className="broker-spotlight__contact-item" aria-label={`Email broker at ${email}`}>
              <img src={EMAIL_ICON} alt="" className="broker-spotlight__contact-icon-img" />
              <span>{email}</span>
            </a>
          )}
          {website && (
            <a href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noopener noreferrer" className="broker-spotlight__contact-item" aria-label="Visit broker website">
              <img src={WEBSITE_ICON} alt="" className="broker-spotlight__contact-icon-img" />
              <span>View Website</span>
            </a>
          )}
          {linkedInUrl && (
            <a href={linkedInUrl.startsWith('http') ? linkedInUrl : `https://${linkedInUrl}`} target="_blank" rel="noopener noreferrer" className="broker-spotlight__contact-item" aria-label="View broker LinkedIn profile">
              <img src={LINKEDIN_ICON} alt="" className="broker-spotlight__contact-icon-img" />
              <span>View LinkedIn Profile</span>
            </a>
          )}
          {bio && (
            <button type="button" className="broker-spotlight__bio-link" onClick={onViewBio}>
              <img src={BIO_ICON} alt="" className="broker-spotlight__contact-icon-img" />
              <span>View Bio</span>
            </button>
          )}
        </div>
      </div>

      <div className="broker-spotlight__card-right">
        {(phone || email) && (
          <div className="broker-spotlight__contact-wrapper">
            <button
              type="button"
              className="broker-spotlight__contact-btn"
              onClick={() => {
                setShowContactPopup(!showContactPopup);
                onContact?.();
              }}
            >
              Contact
            </button>
            {showContactPopup && (
              <div className="broker-spotlight__contact-popup">
                <div className="broker-spotlight__contact-popup-header">
                  <span>Contact</span>
                  <button type="button" onClick={() => setShowContactPopup(false)} className="broker-spotlight__contact-popup-close" aria-label="Close contact options">&times;</button>
                </div>
                {phone && (
                  <a href={`tel:${phone}`} className="broker-spotlight__contact-popup-item">
                    <img src={PHONE_ICON} alt="" className="broker-spotlight__contact-popup-icon" />
                    <span>{phone}</span>
                  </a>
                )}
                {email && (
                  <a href={`mailto:${email}`} className="broker-spotlight__contact-popup-item">
                    <img src={EMAIL_ICON} alt="" className="broker-spotlight__contact-popup-icon" />
                    <span>{email}</span>
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PlatformStats = () => {
  const cmsData = useSelector(homeCmsData);
  const hubzuDifference = cmsData?.hubzuDifference;

  const stats = [
    {
      value: hubzuDifference?.propertySoldCount || "340K+",
      label: hubzuDifference?.propertySoldHeader || "Properties Sold",
      ariaLabel: "340 thousand plus properties sold",
    },
    {
      value: hubzuDifference?.transactionsCount || "$2.5B+",
      label: hubzuDifference?.transactionsHeader || "Total Transaction Value",
      ariaLabel: "2.5 billion dollars plus in total transaction value",
    },
    {
      value: hubzuDifference?.userCount || "2.7M+",
      label: hubzuDifference?.userHeader || "Registered Users",
      ariaLabel: "2.7 million plus registered users",
    },
  ];

  return (
    <div className="broker-spotlight__stats">
      <p className="broker-spotlight__stats-header">Trusted by investors nationwide</p>
      <div className="broker-spotlight__stats-row">
        {stats.map((stat, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <div className="broker-spotlight__stat-divider" />}
            <div className="broker-spotlight__stat" role="group" aria-label={stat.ariaLabel}>
              <span className="broker-spotlight__stat-value">{stat.value}</span>
              <span className="broker-spotlight__stat-label">{stat.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const AuctionInfo = ({ auctioneerDetail }) => {
  // auctioneerDetail is now the direct auctioneer object (or may have nested .auctioneerDetail for backward compat)
  const auctioneer = auctioneerDetail?.auctioneerLcnsTxtOne ? auctioneerDetail : auctioneerDetail?.auctioneerDetail;
  if (!auctioneer) return null;

  const name = auctioneer.auctioneerLcnsTxtOne;
  const licenseNum = auctioneer.auctioneerLcnsNumOne;
  const phone = auctioneer.auctioneerPhoneOne;

  // Only render if at least one piece of data exists
  if (!name && !licenseNum && !phone) return null;

  return (
    <div className="broker-spotlight__auction">
      <h3 className="broker-spotlight__auction-title">Auction Information</h3>
      {name && <p className="broker-spotlight__auction-name">{name}</p>}
      {licenseNum && (
        <p className="broker-spotlight__auction-license">
          License No. {licenseNum}
        </p>
      )}
      {phone && (
        <a
          href={`tel:${phone}`}
          className="broker-spotlight__auction-phone"
          aria-label={`Call auctioneer at ${phone}`}
        >
          {phone}
        </a>
      )}
    </div>
  );
};

const extractSrcFromIframe = (iframeString) => {
  if (!iframeString) return iframeString;
  const match = iframeString.match(/src=["']?([^\s"']+)/);
  return match ? match[1] : iframeString;
};

const VideoWalkthrough = ({ videoData }) => {
  const [showVideoModal, setShowVideoModal] = React.useState(false);

  if (!videoData || !Array.isArray(videoData) || videoData.length === 0) return null;

  const firstVideo = videoData.find((v) => v?.virtualTourUrl);
  if (!firstVideo) return null;

  const videoSrc = extractSrcFromIframe(firstVideo.virtualTourUrl);

  return (
    <div className="broker-spotlight__walkthrough">
      <div className="broker-spotlight__walkthrough-card">
        <div className="broker-spotlight__walkthrough-thumbnail">
          <div
            className="broker-spotlight__walkthrough-poster"
            onClick={() => setShowVideoModal(true)}
            role="button"
            tabIndex={0}
            aria-label="Play property walkthrough video"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowVideoModal(true); }}
          >
            <div className="broker-spotlight__walkthrough-play-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="broker-spotlight__walkthrough-info">
          <h3 className="broker-spotlight__walkthrough-title">Property Walkthrough</h3>
          <p className="broker-spotlight__walkthrough-desc">
            Join the lead broker for a detailed 60-second walkthrough highlighting the property&apos;s key features, recent renovations, and investment potential. Get insider insights on tenant mix, rental upside, and market positioning.
          </p>
          <button
            type="button"
            className="broker-spotlight__walkthrough-btn"
            onClick={() => setShowVideoModal(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 5v14l11-7z" />
            </svg>
            Watch Video Walkthrough
          </button>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="broker-spotlight__video-modal-overlay" onClick={() => setShowVideoModal(false)} role="dialog" aria-modal="true" aria-label="Property walkthrough video">
          <div className="broker-spotlight__video-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="broker-spotlight__video-modal-close"
              onClick={() => setShowVideoModal(false)}
              aria-label="Close video"
            >
              &times;
            </button>
            <iframe
              src={videoSrc}
              title="Property Walkthrough"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="broker-spotlight__video-modal-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const BrokerSpotlight = ({ listingAgentDetail, coListingAgentDetail, auctioneerDetail, videoData, agentDocumentDetail, agentDocumentDetailList = [] }) => {
  const [bioModalBroker, setBioModalBroker] = useState(null);
  const [agentPhotos, setAgentPhotos] = useState({});
  const pdpInitData = useSelector((state) => state.pdp.pdpInitData);

  // Load agent photos from photoPath using the same API as residential PDP
  useEffect(() => {
    const loadPhotos = async () => {
      const photos = {};
      for (const agent of agentDocumentDetailList) {
        if (agent?.photoPath) {
          try {
            const userId = agent.photoPath.split('/')[1];
            if (userId) {
              const url = await getProfilePhotoUrl(userId, agent.photoPath);
              if (url) photos[agent.photoPath] = url;
            }
          } catch (error) {
            // Silently fail, will show default photo
          }
        }
      }
      setAgentPhotos(photos);
    };

    if (agentDocumentDetailList.length > 0) {
      loadPhotos();
    }
  }, [agentDocumentDetailList]);

  const handleContactClick = () => {
    trackButtonClick({
      eventName: "broker_contact_click",
      eventCategory: "NEW-PDP",
      eventSource: "on click",
      eventLabel: buildEventLabel(pdpInitData),
    });
  };

  const handleBioView = (brokerData) => {
    trackButtonClick({
      eventName: "broker_bio_view",
      eventCategory: "NEW-PDP",
      eventSource: "on click",
      eventLabel: buildEventLabel(pdpInitData),
    });
    setBioModalBroker(brokerData);
  };

  // Guard: only render when listing agent data exists
  if (!listingAgentDetail) {
    return null;
  }

  // Extract additional agent fields from agentDocumentDetailList
  const agentDoc = agentDocumentDetail || {};


  // Additional agents from agentDocumentDetailList (skip the first one which is the listing agent)
  const additionalAgents = agentDocumentDetailList.slice(1);

  return (
    <section className="broker-spotlight" id="Brokers">
      <div className="broker-spotlight__container">
        <h2 className="broker-spotlight__title">Hubzu & Broker Team</h2>
        <p className="broker-spotlight__subtitle">
          This property is represented by the following brokers. To schedule a tour or ask questions, reach out to them directly.
        </p>

        <div className="broker-spotlight__content">
          <div className="broker-spotlight__cards-wrapper">
            <div className="broker-spotlight__cards">
            <BrokerCard
              firstName={listingAgentDetail.listingAgentFirstName}
              lastName={listingAgentDetail.listingAgentLastName}
              title={agentDoc.brokerTitle || null}
              company={listingAgentDetail.agency || null}
              photoUrl={agentDoc.photoPath ? (agentPhotos[agentDoc.photoPath] || null) : (listingAgentDetail.photoUrl || null)}
              phone={listingAgentDetail.listingAgentPhone || null}
              email={listingAgentDetail.listingAgentEmailId || null}
              website={agentDoc.website || null}
              linkedInUrl={agentDoc.linkedInUrl || null}
              role="Listing Agent"
              bio={agentDoc.agentBio || null}
              onContact={handleContactClick}
              onViewBio={() => handleBioView({
                firstName: listingAgentDetail.listingAgentFirstName,
                lastName: listingAgentDetail.listingAgentLastName,
                title: agentDoc.brokerTitle,
                company: listingAgentDetail.agency,
                photoUrl: agentDoc.photoPath ? (agentPhotos[agentDoc.photoPath] || null) : listingAgentDetail.photoUrl,
                bio: agentDoc.agentBio,
                phone: listingAgentDetail.listingAgentPhone,
                email: listingAgentDetail.listingAgentEmailId,
                website: agentDoc.website,
                linkedInUrl: agentDoc.linkedInUrl,
              })}
            />
            {/* {coListingAgentDetail && (
              <BrokerCard
                firstName={coListingAgentDetail.laName}
                lastName={coListingAgentDetail.laLastName}
                title={coListingAgentDetail.title || null}
                company={coListingAgentDetail.agencyName || null}
                photoUrl={coListingAgentDetail.photoUrl || null}
                phone={coListingAgentDetail.laPhone || null}
                email={coListingAgentDetail.email || null}
                website={coListingAgentDetail.website || null}
                role="Co-Listing Agent"
                bio={coListingAgentDetail.bio || null}
                onContact={handleContactClick}
                onViewBio={() => handleBioView({
                  firstName: coListingAgentDetail.laName,
                  lastName: coListingAgentDetail.laLastName,
                  title: coListingAgentDetail.title,
                  company: coListingAgentDetail.agencyName,
                  photoUrl: coListingAgentDetail.photoUrl,
                  bio: coListingAgentDetail.bio,
                  phone: coListingAgentDetail.laPhone,
                  email: coListingAgentDetail.email,
                  website: coListingAgentDetail.website,
                })}
              />
            )} */}
            {/* Additional agents from agentDocumentDetailList */}
            {additionalAgents.map((agent, index) => {
              const nameParts = (agent.name || '').split(' ');
              const firstName = nameParts[0] || '';
              const lastName = nameParts.slice(1).join(' ') || '';
              const resolvedPhoto = agent.photoPath ? (agentPhotos[agent.photoPath] || null) : null;
              const formattedPhone = agent.phoneNumber
                ? `(${agent.phoneNumber.slice(0,3)}) ${agent.phoneNumber.slice(3,6)}-${agent.phoneNumber.slice(6)}`
                : null;

              return (
                <BrokerCard
                  key={index}
                  firstName={firstName}
                  lastName={lastName}
                  title={agent.brokerTitle || null}
                  company={agent.companyName || null}
                  photoUrl={resolvedPhoto}
                  phone={formattedPhone}
                  email={agent.email || null}
                  website={agent.website || null}
                  linkedInUrl={agent.linkedInUrl || null}
                  role="Agent"
                  bio={agent.agentBio || null}
                  onContact={handleContactClick}
                  onViewBio={() => handleBioView({
                    firstName,
                    lastName,
                    title: agent.brokerTitle,
                    company: agent.companyName,
                    photoUrl: resolvedPhoto,
                    bio: agent.agentBio,
                    phone: formattedPhone,
                    email: agent.email,
                    website: agent.website,
                    linkedInUrl: agent.linkedInUrl,
                  })}
                />
              );
            })}
          </div>
          </div>

          <VideoWalkthrough videoData={videoData} />
          <PlatformStats />
          {/* <AuctionInfo auctioneerDetail={auctioneerDetail} /> */}
        </div>
      </div>

      <BrokerBioModal
        show={!!bioModalBroker}
        onHide={() => setBioModalBroker(null)}
        broker={bioModalBroker}
      />
    </section>
  );
};

export default BrokerSpotlight;
